/**
 * Roving-tabindex helpers for the desktop navigation menu/menubar.
 *
 * A roving tabindex keeps a single tab stop for a group of menu items: the
 * active item is `tabindex="0"` and every other item is `tabindex="-1"`, so the
 * group is entered once with Tab and traversed with the arrow keys. Shared by
 * the L1 menubar (DesktopNavInteractivity) and the open mega-menu panel
 * (DesktopDropdown).
 */

/** Matches the elements that can hold focus inside a menu/menubar. */
export const ROVING_ITEM_SELECTOR = "a[href], button:not([disabled])"

export type RovingDirection = "next" | "prev" | "first" | "last"

/** Collects the focusable menu items within a container, in DOM order. */
export function getRovingItems(container: HTMLElement): HTMLElement[] {
    return Array.from(container.querySelectorAll<HTMLElement>(ROVING_ITEM_SELECTOR))
}

/**
 * Resolves the target index for a roving move, wrapping around the ends. Returns
 * `-1` when there are no items. A `current` of `-1` (focus not yet on any item)
 * resolves `next`/`prev` to the first/last item respectively.
 */
export function resolveRovingIndex(
    current: number,
    length: number,
    direction: RovingDirection
): number {
    if (length === 0) return -1

    switch (direction) {
        case "next":
            return current < 0 ? 0 : (current + 1) % length
        case "prev":
            return current < 0 ? length - 1 : (current - 1 + length) % length
        case "first":
            return 0
        case "last":
            return length - 1
    }
}

/** Sets the roving tabindex so only `activeIndex` is tabbable. */
export function setRovingTabindex(items: HTMLElement[], activeIndex: number): void {
    items.forEach((item, index) => {
        item.tabIndex = index === activeIndex ? 0 : -1
    })
}

/** Makes the item at `index` the single tab stop and moves focus to it. */
export function focusRovingItem(items: HTMLElement[], index: number): void {
    const item = items[index]
    if (!item) return

    setRovingTabindex(items, index)
    item.focus()
}

/**
 * Moves focus to the first navigable link (an `<a href>`) in the container,
 * skipping non-navigating controls such as drill-in category buttons. Falls
 * back to the first focusable item when the container has no links.
 */
export function focusFirstLink(container: HTMLElement): void {
    const items = getRovingItems(container)
    if (items.length === 0) return

    const linkIndex = items.findIndex(item => item.tagName === "A")
    focusRovingItem(items, linkIndex >= 0 ? linkIndex : 0)
}

/**
 * Moves focus to the first focusable item within `sectionSelector` (e.g. the L2
 * or L3 column), keeping the roving tabindex consistent across the whole
 * container. Falls back to the container's first item when the section is
 * absent or empty.
 */
export function focusFirstInSection(
    container: HTMLElement,
    sectionSelector: string
): void {
    const items = getRovingItems(container)
    if (items.length === 0) return

    const section = container.querySelector<HTMLElement>(sectionSelector)
    const target = section?.querySelector<HTMLElement>(ROVING_ITEM_SELECTOR) ?? null
    const index = target ? items.indexOf(target) : 0

    focusRovingItem(items, index >= 0 ? index : 0)
}
